import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./authContext";

export type ViewRole = "sys-admin" | "administrator" | "teacher" | "student";

const STORAGE_KEY = "friexp.viewAs";

const ViewContext = createContext<
  { viewAs: ViewRole; setViewAs: (role: ViewRole) => void } | undefined
>(undefined);

export const useView = () => {
  const context = useContext(ViewContext);
  if (!context) throw new Error("useView must be used with <ViewProvider>");
  return context;
};

const isViewRole = (v: string): v is ViewRole =>
  v === "sys-admin" ||
  v === "administrator" ||
  v === "teacher" ||
  v === "student";

export const ViewProvider = ({ children }: { children: React.ReactNode }) => {
  const { state: authState } = useAuth();

  // Initialize ONLY from storage (or default) so we never touch authState.user during render
  const [viewAs, setViewAs] = useState<ViewRole>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored && isViewRole(stored) ? stored : "student";
  });

  // Persist to storage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, viewAs);
  }, [viewAs]);

  // If no stored view, default to the user's role once it exists
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return;

    const role = authState.user?.role;
    if (!role) return;

    // only set if role is one of the ViewRole values; otherwise fallback
    setViewAs(isViewRole(role) ? role : "student");
  }, [authState.user]);

  const value = useMemo(() => ({ viewAs, setViewAs }), [viewAs]);

  return <ViewContext.Provider value={value}>{children}</ViewContext.Provider>;
};
